import { z } from 'zod';

/**
 * The one shape every office list endpoint takes and returns, so a table's
 * paging, search, sorting and filters work the same on every screen — see
 * CLAUDE.md → Lists and tables (gitaumoses4/chukua#231).
 */

export const SORT_ORDERS = ['asc', 'desc'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const SEARCH_MAX_LENGTH = 100;
const FILTER_MAX_VALUES = 50;

interface ListQueryOptions<F extends string, S extends z.ZodRawShape> {
  /** Whitelist of sortable fields — the only values `sort` accepts, so a client can never order by an arbitrary column. */
  sortFields: readonly [F, ...F[]];
  defaultSort: { sort: NoInfer<F>; order: SortOrder };
  /** The screen's own filters, each built with `listFilter()` so a table column filter maps onto it directly. */
  filters?: S;
}

/** `?page=2&pageSize=25&q=wanjiru&sort=name&order=asc&<filter>=…` — query-string friendly (coerced numbers, repeated keys for arrays). */
export function defineListQuery<F extends string, S extends z.ZodRawShape = Record<never, never>>({
  sortFields,
  defaultSort,
  filters,
}: ListQueryOptions<F, S>) {
  return z
    .object({
      page: z.coerce.number().int().min(1).default(1),
      pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
      q: z
        .string()
        .trim()
        .max(SEARCH_MAX_LENGTH)
        .optional()
        .transform((q) => q || undefined),
      sort: z.enum(sortFields as unknown as [F, ...F[]]).default(defaultSort.sort as F),
      order: z.enum(SORT_ORDERS).default(defaultSort.order),
    })
    .extend((filters ?? {}) as S);
}

/**
 * A multi-value column filter: `?role=admin&role=finance` or a single `?role=admin`.
 * A query string gives one value as a string and repeats as an array; both become an array here.
 */
export function listFilter<T extends z.ZodType>(value: T) {
  return z.preprocess((raw) => (raw === undefined || Array.isArray(raw) ? raw : [raw]), z.array(value).max(FILTER_MAX_VALUES).optional());
}

/**
 * `?current=true` (in force today) or `?current=false` (not); `queryFlag` is the same idea for any yes/no filter (`?mine=true`). A query
 * string only carries text, and `z.coerce.boolean()` would read "false" as true, so the two words are matched exactly.
 */
export const queryFlag = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();

export const inForceFilter = queryFlag;

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
